/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { ShopperExperience } from '@/scapi';

type RegionComponent = ShopperExperience.schemas['Component'];

type RegionSiblingsContextValue = {
    components: readonly RegionComponent[];
};

const RegionSiblingsContext = createContext<RegionSiblingsContextValue | null>(null);

export function RegionSiblingsProvider({
    components,
    children,
}: {
    components: readonly RegionComponent[];
    children: ReactNode;
}) {
    return <RegionSiblingsContext.Provider value={{ components }}>{children}</RegionSiblingsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRegionSiblings(): RegionSiblingsContextValue | null {
    return useContext(RegionSiblingsContext);
}
